import { useMemo } from 'react';
import { API_CONFIG } from '../lib/apiConfig';

interface UseAgbOverlayResult {
  /** Leaflet-ready tile template with {z}/{x}/{y} placeholders. */
  tileUrl: string;
  /** Config-declared year bounds so the slider control derives its range. */
  minYear: number;
  maxYear: number;
  /**
   * Bottom of the source pyramid. Below this Leaflet would 413 on the
   * upstream scraper and (for the cached pyramid) 404 — we just don't
   * try.
   */
  minZoom: number;
  /**
   * Top of the source pyramid. Plumbed to TileLayer as `maxNativeZoom`
   * so Leaflet upscales the z=10 tiles past z=10 instead of fetching
   * tiles that don't exist.
   */
  maxNativeZoom: number;
  /**
   * Rescale used by AgbMapLegend (Mg/ha). The pyramid was baked with the
   * server-default rescale, so the legend domain is approximate — kept in
   * sync with the basemap legend so both agree on labels.
   */
  rescale: [number, number];
}

/**
 * Resolves an S3 tile URL for the AGB 100m overlay at the given `year`.
 * The pyramid is baked offline by scripts/scrape-agb-overlay-tiles.py and
 * uploaded to s3://ctrees-website/sfi/agb/tiles/{year}/{z}/{x}/{y}.png,
 * served with `Cache-Control: max-age=31536000, immutable` — so a year
 * scrub is one CDN GET per tile (cold) and a memory-cache hit thereafter.
 *
 * The S3 pyramid is public and always available, so this hook has no
 * "unavailable" branch — enable/disable is purely a render-time concern
 * for callers (whether to mount the overlay components at all).
 */
export const useAgbOverlay = (year: number): UseAgbOverlayResult => {
  return useMemo(() => {
    const cfg = API_CONFIG.agbOverlay;
    return {
      tileUrl: `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.agbOverlayTile(year)}`,
      minYear: cfg.minYear,
      maxYear: cfg.maxYear,
      minZoom: cfg.minZoom,
      maxNativeZoom: cfg.maxNativeZoom,
      rescale: cfg.legendRescale,
    };
  }, [year]);
};
